import { 
    QueryKey, 
    useInfiniteQuery, 
    useQueryClient, 
    UseInfiniteQueryOptions
} from 'react-query';
import { buildURL, QueryParams, Encodable } from '../utils/buildURL';
import { fetcher } from '../utils/fetcher';

type PageParam = Record<string, Encodable | Encodable[]> & {
    page?: number;
    cursor?: string;
    offset?: number;
};

type Invalidate = () => void;

/**
 * Helper method for useInfiniteQuery (https://react-query.tanstack.com/reference/useInfiniteQuery)
 *
 * Added:
 * - url + params are used as the key
 * - `mutation` helper method inspired by useSWR.
 * - `isValidating` helper value inspired by useSWR
 * - `invalidate` helper method to invalidate and refetch data
 * - Support for infinite loading with pagination
 *
 * Using React Query over useSWR to future proof the codebase.
 * Read all available benefits here:
 * NOTE: not all features are implemented yet.
 * @see https://react-query.tanstack.com/comparison
 */
export const useGetInfiniteQuery = <T, E = unknown>(
    url: string,
    options?: UseInfiniteQueryOptions<T, E, T, T, QueryKey> & { 
        params?: QueryParams; 
        headers?: HeadersInit;
        getNextPageParam?: (lastPage: T, allPages: T[]) => PageParam | undefined;
        initialPageParam?: PageParam;
        pageSize?: number;
    }
) => {
    const { 
        params, 
        headers, 
        getNextPageParam = (_lastPage: T, _allPages: T[]) => {
            // Default implementation - override this for your specific API
            return undefined;
        },
        initialPageParam = { page: 0 },
        pageSize = 5,
        ...otherOptions 
    } = options || {};

    // Merge pageSize into params
    const baseParams = { ...params, pageSize };

    // Generate a deterministic URL based on url + params (without page params)
    const baseUrl = buildURL(url, baseParams);
    const queryClient = useQueryClient();

    // Create fetcher that handles page parameters
    const infiniteFetcher = async ({ pageParam = initialPageParam }: { pageParam?: PageParam }) => {
        const pageParams: QueryParams = { ...baseParams, ...pageParam };
        const paginatedUrl = buildURL(url, pageParams);
        return fetcher<T>(paginatedUrl, { headers })();
    };

    // Use deterministic generated URL as key
    const query = useInfiniteQuery<T, E>(
        [baseUrl, 'infinite'],
        infiniteFetcher,
        {
            getNextPageParam,
            ...otherOptions
        }
    );

    const isValidating = query.isFetching;

    // Helper method for invalidating
    const invalidate: Invalidate = () => queryClient.invalidateQueries([baseUrl, 'infinite']);

    // Helper method for mutating the stored state
    const mutate = (newValue: T[], invalidateQuery = false) => {
        queryClient.setQueryData([baseUrl, 'infinite'], () => ({
            pages: newValue,
            pageParams: []
        }));
        if (invalidateQuery) {
            invalidate();
        }
    };

    return {
        ...query,
        isValidating,
        mutate,
        invalidate,
        loadMore: query.fetchNextPage,
        canLoadMore: query.hasNextPage ?? false,
        isLoadingMore: query.isFetchingNextPage,
    };
};
